<template>
  <div class="tracker-panel">

    <div class="grid grid-4">
      <div class="metric-card">
        <div class="metric-card-label">Short Units P&L</div>
        <div class="metric-card-value" :class="totals.totalPnl >= 0 ? 'profit-positive' : 'profit-negative'">
          {{ totals.totalPnl.toFixed(2) }} USD
        </div>
        <div class="metric-card-secondary" :class="totals.pnlPct >= 0 ? 'profit-positive' : 'profit-negative'">{{ totals.pnlPct.toFixed(2) }}%</div>
      </div>
      <div class="metric-card">
        <div class="metric-card-label">Qty Above Market</div>
        <div class="metric-card-value profit-positive">{{ totals.qtyAboveMkt.toFixed(6) }} BTC</div>
        <div class="metric-card-secondary">Above PMP: {{ totals.qtyAbovePMP.toFixed(6) }} BTC</div>
      </div>
      <div class="metric-card">
        <div class="metric-card-label">P&L Positivo Total</div>
        <div class="metric-card-value profit-positive">{{ totals.pnlPositive.toFixed(2) }} USD</div>
      </div>
      <div class="metric-card">
        <div class="metric-card-label">Posição Short Total</div>
        <div class="metric-card-value">{{ totals.remainingQty.toFixed(6) }} BTC</div>
        <div class="metric-card-secondary">
          Avg: {{ totals.avgSellPrice.toFixed(2) }} | Notional: {{ totals.notional.toFixed(2) }}
        </div>
      </div>
    </div>

    <div class="units-section">
      <div class="units-label">Short Units</div>
      <div v-if="activeUnits.length" class="units-list grid grid-3 scrollable">
        <div v-for="unit in activeUnits" :key="unit.shortUnitID" class="unit-chip" :class="{ 'profit-chip': unrealizedPnl(unit) >= 0, 'loss-chip': unrealizedPnl(unit) < 0 }">
          <div class="unit-badge">{{ unit.instrument }}</div>
          <div class="unit-id">{{ unit.shortUnitID }}</div>
          <div class="unit-sub">Price: {{ unit.origin.sellPrice.toFixed(2) }} | Qty: {{ unit.remainingQty.toFixed(6) }}</div>
          <div class="unit-sub unit-target">Target: {{ targetPrice(unit).toFixed(2) }} (-{{ Number(targetMarginPct).toFixed(1) }}%)</div>
          <div class="unit-sub" :class="unrealizedPnl(unit) >= 0 ? 'profit-positive' : 'profit-negative'">
            P&L: {{ unrealizedPnl(unit).toFixed(2) }} ({{ pnlPct(unit) }}%)
          </div>
        </div>
      </div>
      <div v-else class="empty-msg">Nenhuma short unit ativa.</div>
    </div>

  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'shortUnitsTracker',
  computed: {
    ...mapState({
      shortLedger:     state => state.shortLedger,
      ledger:          state => state.ledger,
      btcPrice:        state => state.marketData.btcUsd,
      targetMarginPct: state => state.targetMarginPct,
    }),
    ...mapGetters(['targetPctFrac']),

    activeUnits() {
      return Object.values(this.shortLedger || {}).filter(u => u.status === 'active').sort((a, b) => b.origin.sellPrice - a.origin.sellPrice);
    },

    totals() {
      const remainingQty = this.activeUnits.reduce((s, u) => s + Number(u.remainingQty || 0), 0);
      const totalValue = this.activeUnits.reduce((s, u) => s + Number(u.origin?.sellPrice || 0) * Number(u.remainingQty || 0), 0);
      const avgSellPrice = remainingQty > 0 ? totalValue / remainingQty : 0;
      const qtyAboveMkt = this.activeUnits.filter(u => Number(u.origin?.sellPrice || 0) > this.btcPrice).reduce((s, u) => s + Number(u.remainingQty || 0), 0);
      const pnlPositive = this.activeUnits.reduce((s, u) => { const p = this.unrealizedPnl(u); return p > 0 ? s + p : s; }, 0);
      const manualUnits = Object.values(this.ledger || {}).filter(u => u.bucket === 'manual');
      const manualQty = manualUnits.reduce((s, u) => s + Number(u.current?.qty || 0), 0);
      const manualVal = manualUnits.reduce((s, u) => s + Number(u.current?.avgPrice || 0) * Number(u.current?.qty || 0), 0);
      const manualPMP = manualQty > 0 ? manualVal / manualQty : 0;
      const qtyAbovePMP = this.activeUnits.filter(u => Number(u.origin?.sellPrice || 0) > manualPMP).reduce((s, u) => s + Number(u.remainingQty || 0), 0);
      return {
        remainingQty,
        avgSellPrice: Math.round(avgSellPrice * 100) / 100,
        notional: Math.round(totalValue * 100) / 100,
        totalPnl: Math.round((avgSellPrice - this.btcPrice) * remainingQty * 100) / 100,
        pnlPct: totalValue > 0 ? Math.round((avgSellPrice - this.btcPrice) / avgSellPrice * 10000) / 100 : 0,
        qtyAboveMkt: Math.round(qtyAboveMkt * 1e6) / 1e6,
        qtyAbovePMP: Math.round(qtyAbovePMP * 1e6) / 1e6,
        pnlPositive: Math.round(pnlPositive * 100) / 100,
      };
    },
  },

  methods: {
    unrealizedPnl(unit) {
      const qty = Number(unit.remainingQty || 0);
      const sell = Number(unit.origin?.sellPrice || 0);
      return Math.round((sell - this.btcPrice) * qty * 100) / 100;
    },
    pnlPct(unit) {
      const sell = Number(unit.origin?.sellPrice || 0);
      return sell > 0 ? ((sell - this.btcPrice) / sell * 100).toFixed(2) : '0.00';
    },
    targetPrice(unit) {
      return (unit.origin?.sellPrice || 0) * (1 - this.targetPctFrac);
    },
  },
};
</script>

<style scoped>

.units-list {
    height: 400px !important;
}

.unit-target {
  color: #e3b341;
  opacity: 1;
}
</style>
